import React, { useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import Portal from "components/layout/Portal";
import { setMicrophoneSettings, subscribeMicrophoneVolume } from "lib/audioTracksApi";
import type { MicrophoneVolumePayload, MicrophoneVolumeUpdate } from "lib/audioTracksApi";

const SAVE_DEBOUNCE_MS = 120;
const SERVER_SETTLE_GUARD_MS = 500;
const INITIAL_EVENT_TIMEOUT_MS = 8000;

type VolumeView = {
    targetKey: string;
    volume: number | null;
    draft: number | null;
    mute: boolean | null;
    draftMute: boolean | null;
    loading: boolean;
    loadError: boolean;
    saveError: boolean;
};

const emptyView = (targetKey: string): VolumeView => ({
    targetKey,
    volume: null,
    draft: null,
    mute: null,
    draftMute: null,
    loading: true,
    loadError: false,
    saveError: false,
});

interface MicrophoneVolumeButtonProps {
    arcId: string | null;
    proxyHost: string | null;
    isMicActive?: boolean;
}

/** Adjusts the volume of microphone audio played through the robot speaker. */
const MicrophoneVolumeButton: React.FC<MicrophoneVolumeButtonProps> = ({ arcId, proxyHost, isMicActive = false }) => {
    const { t } = useTranslation("control");
    const titleId = useId();
    const sliderId = useId();
    const targetKey = `${arcId ?? "direct"}\u0000${proxyHost?.trim() ?? ""}`;
    const targetKeyRef = useRef(targetKey);
    targetKeyRef.current = targetKey;
    const activeGenerationRef = useRef(0);

    const [view, setView] = useState<VolumeView>(() => emptyView(targetKey));
    const currentView = view.targetKey === targetKey ? view : emptyView(targetKey);
    const [retryCount, setRetryCount] = useState(0);
    const [open, setOpen] = useState(false);
    const [position, setPosition] = useState({ top: 8, left: 8 });
    const [positionReady, setPositionReady] = useState(false);
    const triggerRef = useRef<HTMLButtonElement>(null);
    const popoverRef = useRef<HTMLDivElement>(null);

    const latestServerSettingsRef = useRef<MicrophoneVolumePayload | null>(null);
    const lastConfirmedSettingsRef = useRef<MicrophoneVolumePayload | null>(null);
    const serverEventVersionRef = useRef(0);
    const saveErrorServerEventVersionRef = useRef<number | null>(null);
    const pendingSettingsRef = useRef<MicrophoneVolumeUpdate | null>(null);
    const editVersionRef = useRef(0);
    const guardUntilRef = useRef(0);
    const draggingRef = useRef(false);
    const sendingRef = useRef(false);
    const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const guardTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const applyServerSettings = useCallback((settings: MicrophoneVolumePayload, key: string, preserveSaveError = false) => {
        if (targetKeyRef.current !== key) return;
        lastConfirmedSettingsRef.current = settings;
        if (!preserveSaveError) saveErrorServerEventVersionRef.current = null;
        setView((previous) => ({
            ...emptyView(key),
            volume: settings.volume,
            draft: settings.volume,
            mute: settings.mute,
            draftMute: settings.mute,
            loading: false,
            saveError: preserveSaveError && previous.targetKey === key && previous.saveError,
            targetKey: key,
        }));
    }, []);

    const reconcileServerVolume = useCallback((key: string, generation: number) => {
        if (targetKeyRef.current !== key || activeGenerationRef.current !== generation) return;
        if (
            draggingRef.current ||
            sendingRef.current ||
            pendingSettingsRef.current !== null ||
            debounceTimerRef.current !== null
        ) {
            return;
        }
        const remaining = guardUntilRef.current - Date.now();
        if (remaining > 0) {
            if (guardTimerRef.current) clearTimeout(guardTimerRef.current);
            guardTimerRef.current = setTimeout(() => {
                guardTimerRef.current = null;
                reconcileServerVolume(key, generation);
            }, remaining);
            return;
        }
        const latest = latestServerSettingsRef.current;
        if (latest !== null) {
            const failedAtVersion = saveErrorServerEventVersionRef.current;
            const preserveSaveError = failedAtVersion !== null && serverEventVersionRef.current <= failedAtVersion;
            applyServerSettings(latest, key, preserveSaveError);
        }
    }, [applyServerSettings]);

    const scheduleReconciliation = useCallback((key: string) => {
        const generation = activeGenerationRef.current;
        if (guardTimerRef.current) clearTimeout(guardTimerRef.current);
        const remaining = Math.max(0, guardUntilRef.current - Date.now());
        guardTimerRef.current = setTimeout(() => {
            guardTimerRef.current = null;
            reconcileServerVolume(key, generation);
        }, remaining);
    }, [reconcileServerVolume]);

    const flushPendingSettings = useCallback(async () => {
        if (sendingRef.current || pendingSettingsRef.current === null) return;
        const update = pendingSettingsRef.current;
        pendingSettingsRef.current = null;
        const sentEditVersion = editVersionRef.current;
        const sentTargetKey = targetKey;
        const sentGeneration = activeGenerationRef.current;
        const sentServerEventVersion = serverEventVersionRef.current;
        const isCurrentRequest = () =>
            targetKeyRef.current === sentTargetKey && activeGenerationRef.current === sentGeneration;
        sendingRef.current = true;
        guardUntilRef.current = Date.now() + SERVER_SETTLE_GUARD_MS;
        scheduleReconciliation(sentTargetKey);
        setView((previous) => previous.targetKey === sentTargetKey
            ? { ...previous, saveError: false }
            : previous);

        try {
            const savedSettings = await setMicrophoneSettings(update, arcId, proxyHost);
            if (!isCurrentRequest()) return;
            const newerServerEvent = serverEventVersionRef.current !== sentServerEventVersion;
            if (!newerServerEvent) {
                latestServerSettingsRef.current = savedSettings;
                lastConfirmedSettingsRef.current = savedSettings;
            }
            saveErrorServerEventVersionRef.current = null;
            setView((previous) => {
                if (previous.targetKey !== sentTargetKey) return previous;
                const newerAdjustment =
                    editVersionRef.current !== sentEditVersion ||
                    pendingSettingsRef.current !== null ||
                    debounceTimerRef.current !== null ||
                    draggingRef.current;
                return {
                    ...previous,
                    volume: newerServerEvent ? previous.volume : savedSettings.volume,
                    draft: newerAdjustment || newerServerEvent ? previous.draft : savedSettings.volume,
                    mute: newerServerEvent ? previous.mute : savedSettings.mute,
                    draftMute: newerAdjustment || newerServerEvent ? previous.draftMute : savedSettings.mute,
                    loading: false,
                    saveError: false,
                };
            });
        } catch {
            if (isCurrentRequest()) {
                const newerAdjustment =
                    editVersionRef.current !== sentEditVersion ||
                    pendingSettingsRef.current !== null ||
                    debounceTimerRef.current !== null ||
                    draggingRef.current;
                if (!newerAdjustment) {
                    saveErrorServerEventVersionRef.current = serverEventVersionRef.current;
                    const rollback = latestServerSettingsRef.current ?? lastConfirmedSettingsRef.current;
                    if (rollback !== null) {
                        setView((previous) => previous.targetKey === sentTargetKey
                            ? {
                                ...previous,
                                volume: rollback.volume,
                                draft: rollback.volume,
                                mute: rollback.mute,
                                draftMute: rollback.mute,
                                saveError: true,
                            }
                            : previous);
                    } else {
                        setView((previous) => previous.targetKey === sentTargetKey
                            ? { ...previous, saveError: true }
                            : previous);
                    }
                }
            }
        } finally {
            if (isCurrentRequest()) {
                sendingRef.current = false;
                if (pendingSettingsRef.current !== null && debounceTimerRef.current === null) {
                    void flushPendingSettings();
                } else {
                    scheduleReconciliation(sentTargetKey);
                }
            }
        }
    }, [arcId, proxyHost, scheduleReconciliation, targetKey]);

    useEffect(() => {
        let active = true;
        const key = targetKey;
        const generation = ++activeGenerationRef.current;
        latestServerSettingsRef.current = null;
        lastConfirmedSettingsRef.current = null;
        serverEventVersionRef.current = 0;
        saveErrorServerEventVersionRef.current = null;
        pendingSettingsRef.current = null;
        editVersionRef.current = 0;
        guardUntilRef.current = 0;
        draggingRef.current = false;
        sendingRef.current = false;
        if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
        if (guardTimerRef.current) clearTimeout(guardTimerRef.current);
        debounceTimerRef.current = null;
        guardTimerRef.current = null;
        setView(emptyView(key));

        const initialTimeout = setTimeout(() => {
            if (!active || targetKeyRef.current !== key || latestServerSettingsRef.current !== null) return;
            setView((previous) => previous.targetKey === key
                ? { ...previous, loading: false, loadError: true }
                : previous);
        }, INITIAL_EVENT_TIMEOUT_MS);

        let unsubscribe: (() => void) | null = null;
        try {
            unsubscribe = subscribeMicrophoneVolume(
                arcId,
                proxyHost,
                (settings) => {
                    if (!active || targetKeyRef.current !== key) return;
                    serverEventVersionRef.current += 1;
                    clearTimeout(initialTimeout);
                    latestServerSettingsRef.current = settings;
                    const hasLocalAdjustment =
                        draggingRef.current ||
                        sendingRef.current ||
                        pendingSettingsRef.current !== null ||
                        debounceTimerRef.current !== null ||
                        Date.now() < guardUntilRef.current;
                    if (hasLocalAdjustment) {
                        setView((previous) => previous.targetKey === key
                            ? {
                                ...previous,
                                loading: false,
                                loadError: false,
                                volume: previous.volume ?? settings.volume,
                                mute: previous.mute ?? settings.mute,
                            }
                            : previous);
                        scheduleReconciliation(key);
                    } else {
                        applyServerSettings(settings, key);
                    }
                },
                () => {
                    if (!active || targetKeyRef.current !== key || latestServerSettingsRef.current !== null) return;
                    setView((previous) => previous.targetKey === key
                        ? { ...previous, loading: false, loadError: true }
                        : previous);
                },
            );
        } catch {
            setView((previous) => previous.targetKey === key
                ? { ...previous, loading: false, loadError: true }
                : previous);
        }

        return () => {
            active = false;
            if (activeGenerationRef.current === generation) activeGenerationRef.current += 1;
            clearTimeout(initialTimeout);
            unsubscribe?.();
            if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
            if (guardTimerRef.current) clearTimeout(guardTimerRef.current);
            debounceTimerRef.current = null;
            guardTimerRef.current = null;
            pendingSettingsRef.current = null;
            draggingRef.current = false;
            sendingRef.current = false;
        };
    }, [arcId, applyServerSettings, proxyHost, retryCount, scheduleReconciliation, targetKey]);

    const handleSliderChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const volume = event.currentTarget.valueAsNumber;
        if (currentView.loading || currentView.draft === null) return;
        editVersionRef.current += 1;
        saveErrorServerEventVersionRef.current = null;
        pendingSettingsRef.current = { ...pendingSettingsRef.current, volume };
        setView((previous) => previous.targetKey === targetKey
            ? { ...previous, draft: volume, saveError: false }
            : previous);
        if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = setTimeout(() => {
            debounceTimerRef.current = null;
            void flushPendingSettings();
        }, SAVE_DEBOUNCE_MS);
    };

    const commitSliderChange = () => {
        draggingRef.current = false;
        if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = null;
        if (pendingSettingsRef.current !== null) {
            void flushPendingSettings();
        } else {
            scheduleReconciliation(targetKey);
        }
    };

    const toggleMute = () => {
        if (currentView.loading || currentView.draftMute === null) return;
        editVersionRef.current += 1;
        saveErrorServerEventVersionRef.current = null;
        draggingRef.current = false;
        const mute = !currentView.draftMute;
        pendingSettingsRef.current = { ...pendingSettingsRef.current, mute };
        setView((previous) => previous.targetKey === targetKey
            ? { ...previous, draftMute: mute, saveError: false }
            : previous);
        if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = null;
        void flushPendingSettings();
    };

    useEffect(() => {
        if (!open) return undefined;
        const onOutsidePointerDown = (event: PointerEvent) => {
            const target = event.target as Node;
            if (triggerRef.current?.contains(target) || popoverRef.current?.contains(target)) return;
            setOpen(false);
        };
        const onKeyDown = (event: KeyboardEvent) => {
            if (event.key === "Escape") {
                setOpen(false);
                triggerRef.current?.focus();
            }
        };
        document.addEventListener("pointerdown", onOutsidePointerDown);
        document.addEventListener("keydown", onKeyDown);
        return () => {
            document.removeEventListener("pointerdown", onOutsidePointerDown);
            document.removeEventListener("keydown", onKeyDown);
        };
    }, [open]);

    useLayoutEffect(() => {
        if (!open) {
            setPositionReady(false);
            return undefined;
        }
        const updatePosition = () => {
            const trigger = triggerRef.current;
            const popover = popoverRef.current;
            if (!trigger || !popover) return;
            const triggerRect = trigger.getBoundingClientRect();
            const popoverRect = popover.getBoundingClientRect();
            const left = Math.max(8, Math.min(
                triggerRect.left + triggerRect.width / 2 - popoverRect.width / 2,
                window.innerWidth - popoverRect.width - 8,
            ));
            const top = Math.max(8, triggerRect.top - popoverRect.height - 8);
            setPosition({ top, left });
            setPositionReady(true);
        };
        updatePosition();
        window.addEventListener("resize", updatePosition);
        window.addEventListener("scroll", updatePosition, true);
        return () => {
            window.removeEventListener("resize", updatePosition);
            window.removeEventListener("scroll", updatePosition, true);
        };
    }, [open, currentView.loading, currentView.loadError, currentView.saveError]);

    const loaded = !currentView.loading && currentView.draft !== null && currentView.draftMute !== null;
    const volume = currentView.draft ?? 0;
    const muted = currentView.draftMute ?? false;
    const mutedWhileLive = loaded && isMicActive && (muted || volume === 0);
    const muteLabel = muted ? t("bottomBar.unmuteMicrophone") : t("bottomBar.muteMicrophone");

    return (
        <div className="inline-flex shrink-0">
            <button
                ref={triggerRef}
                type="button"
                aria-label={t("bottomBar.microphoneVolume")}
                aria-expanded={open}
                aria-controls={open ? titleId : undefined}
                onClick={() => setOpen((previous) => !previous)}
                className={`relative isolate grid h-11 w-11 place-items-center rounded-full ${mutedWhileLive ? "text-on-accent" : "text-white"} ${open && !mutedWhileLive ? "bg-white/10" : ""}`}
            >
                {mutedWhileLive && (
                    <span
                        aria-hidden="true"
                        className="pointer-events-none absolute inset-0 rounded-full bg-red-500/70 motion-safe:animate-microphone-muted motion-reduce:animate-none"
                    />
                )}
                <svg className="relative z-10 h-6 w-6" viewBox="0 0 24 24" aria-hidden="true">
                    <path d="M4 9v6h4l5 5V4L8 9H4z" fill="currentColor" />
                    {muted || volume === 0 ? (
                        <path d="m16 9 5 6m0-6-5 6" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                    ) : (
                        <path d="M16 8a5 5 0 0 1 0 8m2-11a9 9 0 0 1 0 14" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                    )}
                </svg>
            </button>

            <Portal visible={open} target={typeof document === "undefined" ? null : document.body}>
                <div
                    ref={popoverRef}
                    role="dialog"
                    aria-labelledby={titleId}
                    className="fixed z-[100] w-64 rounded-r10 border border-black70 bg-black90 p-3 text-white shadow-xl"
                    style={{ top: position.top, left: position.left, visibility: positionReady ? "visible" : "hidden" }}
                >
                    <h2 id={titleId} className="text-h4m">{t("bottomBar.microphoneVolume")}</h2>

                    {currentView.loading && <p className="mt-3 text-h5l" role="status">{t("bottomBar.microphoneVolumeLoading")}</p>}
                    {currentView.loadError && (
                        <div className="mt-3 flex items-center justify-between gap-3" role="alert">
                            <span className="text-h5l">{t("bottomBar.microphoneVolumeLoadFailed")}</span>
                            <button
                                type="button"
                                className="rounded-r5 bg-black70 px-2.5 py-1.5 text-h5m"
                                onClick={() => {
                                    setView(emptyView(targetKey));
                                    setRetryCount((count) => count + 1);
                                }}
                            >
                                {t("bottomBar.microphoneVolumeRetry")}
                            </button>
                        </div>
                    )}

                    <div className="mt-3 flex items-center gap-3">
                        <button
                            type="button"
                            aria-label={muteLabel}
                            title={muteLabel}
                            aria-pressed={muted}
                            disabled={!loaded}
                            onClick={toggleMute}
                            className={`grid h-8 w-8 shrink-0 place-items-center rounded-r5 disabled:cursor-not-allowed disabled:opacity-40 ${muted ? "bg-primary text-on-accent" : "bg-black70"}`}
                        >
                            <svg className="h-5 w-5" viewBox="0 0 24 24" aria-hidden="true">
                                <path d="M4 9v6h4l5 5V4L8 9H4z" fill="currentColor" />
                                {muted ? (
                                    <path d="M16 8a5 5 0 0 1 0 8" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                                ) : (
                                    <path d="m16 9 5 6m0-6-5 6" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                                )}
                            </svg>
                        </button>
                        <label htmlFor={sliderId} className="sr-only">{t("bottomBar.microphoneVolume")}</label>
                        <input
                            id={sliderId}
                            type="range"
                            min={0}
                            max={100}
                            step={1}
                            value={volume}
                            disabled={!loaded}
                            aria-valuetext={`${volume}%`}
                            onChange={handleSliderChange}
                            onPointerDown={() => { draggingRef.current = true; }}
                            onPointerUp={commitSliderChange}
                            onPointerCancel={commitSliderChange}
                            onKeyUp={commitSliderChange}
                            onBlur={commitSliderChange}
                            className="h-2 min-w-0 flex-1 cursor-pointer accent-primary disabled:cursor-not-allowed disabled:opacity-40"
                        />
                        <output htmlFor={sliderId} className="w-10 text-right text-h4m tabular-nums">
                            {loaded ? `${volume}%` : "—"}
                        </output>
                    </div>

                    {currentView.saveError && (
                        <p className="mt-2 text-h5l text-red-300" role="alert">{t("bottomBar.microphoneVolumeSaveFailed")}</p>
                    )}
                </div>
            </Portal>
        </div>
    );
};

export default MicrophoneVolumeButton;
